
export type RedisConnectionData = {
    username?: string,
    password?: string,
    keyPath?: string,
    certPath?: string,
    caPath? : string | string[],
    host: string,
    port: number
} | string

import { readFileSync  } from 'fs';

function parseConfigPaths(v: string | string[]) : Buffer[] {
    if(v) {
        if(Array.isArray(v)) {
            return v.map(v2 => {
                return readFileSync(v2);
            })
        } else {
            return [readFileSync(v)];
        }
    }
    return [];
}

/**
 * The part of node-redis this file uses. Deliberately not `typeof import('redis')`: this module's declarations are
 * part of @gotti/server's published types, and a consumer without the optional peer must still type-check them.
 */
type RedisModule = { createClient(options: object): unknown };

/**
 * `redis` is an OPTIONAL peer of @gotti/server: only a server whose config sets `redis` needs it, so it is imported
 * here, when that server initializes, and never at module load. Without it installed, the import failure (kept as the
 * `cause`) becomes an error that names the peer and the version this wrapper is written for (node-redis v4).
 */
export async function loadRedis(): Promise<RedisModule> {
  try {
    return await import('redis');
  } catch (cause) {
    throw new Error('@gotti/server needs the optional peer "redis" to connect to Redis (the server\'s config sets `redis`): npm install redis@4', { cause });
  }
}

export class RedisClientWrapper {
  readonly redisClient: any;
  constructor(redisClient: any) {
      this.redisClient = redisClient;
  }

  public static async Init(opts: RedisConnectionData) : Promise<RedisClientWrapper> {
    // Everything before the connect runs in this async function, so a failure there — the missing peer, an unreadable
    // key/cert/ca file — rejects Init. (It used to run inside an `async` Promise executor, whose rejections the Promise
    // never sees: those failures hung `initialize()` forever and surfaced only as an unhandled rejection.)
    const redis = await loadRedis();
    let redisClient: any = null;
    if(typeof opts === "string") {
        redisClient = redis.createClient({ url: opts })
    } else {
        redisClient = redis.createClient({
            username: opts.username,
            password: opts.password,
            socket: {
                host: opts.host,
                port: opts.port,
                key: opts.keyPath ? readFileSync(opts.keyPath) : undefined,
                cert: opts.certPath ? readFileSync(opts.certPath) : undefined,
                ca: opts.caPath ? parseConfigPaths(opts.caPath) : undefined
            }
        })
    }
    // node-redis reports a failed connection attempt as an 'error' event (and retries); the first one fails Init.
    await new Promise<void>((resolve, reject) => {
        const handleError = (err: unknown) => reject(err);
        redisClient.on('error', handleError);
        redisClient.connect().then(() => {
            redisClient.off('error', handleError);
            resolve();
        }, reject);
    });
    return new RedisClientWrapper(redisClient);
  }
  
  on(event: string, listener: Function): void {
    this.redisClient.on(event, listener);
  }

  off(event: string, listener: Function): void {
      this.redisClient.off(event, listener);
  }
  /**
   * EVERY KEY, FIELD AND MEMBER THIS WRAPPER PASSES DOWN IS `String()`d — and
   * that is a correctness requirement, not tidiness.
   *
   * node-redis converts numbers to strings in SOME argument positions and not
   * others, and the ones it does not reach the RESP2 encoder raw, which accepts
   * only strings and Buffers and throws `TypeError: Invalid argument type`.
   * Verified against the installed @redis/client + redis 8.2.1:
   *
   *   HSET(k, 5, v)   OK   — `pushObject`/the scalar form run BOTH the field
   *                          and the value through `convertValue`
   *   SET(k, 7)       OK   — the value is converted
   *   HGET(k, 5)      THROWS — its transform is `['HGET', key, field]`, raw
   *   HDEL(k, 5)      THROWS — same
   *   HMGET(k, [5])   THROWS — same
   *   SADD/SREM/RPUSH THROWS — `pushVerdictArguments` concatenates raw
   *   SET(12345, 'x') THROWS — a numeric KEY is never converted either
   *
   * So the safe positions are a minority and the unsafe ones fail only against
   * a real daemon. Every signature here advertises `string | number` and real
   * callers really do pass numbers (`getPublicIdOwner(publicId)` with
   * `useSequentialPublicIds`, `releaseSequentialId(id)`), so the adapter owes
   * them the conversion uniformly rather than per-command — a per-command rule
   * is what produced a store that threw on every peer-connection request.
   *
   * *** WHY THIS `String()`s A NON-SCALAR INSTEAD OF REJECTING IT. *** Passing
   * an object here yields the key `'[object Object]'` rather than a TypeError,
   * which reads like sloppy hygiene. It was reviewed and kept DELIBERATELY, and
   * the reasons are specific enough to be worth writing down so the next reader
   * does not "fix" it:
   *
   *  - it cannot recreate the fake-vs-daemon blind spot this whole file exists
   *    to close. That defect class needs the FAKE to be laxer than the DAEMON;
   *    `k()` sits ABOVE both, so neither backing ever sees the raw value and the
   *    two cannot diverge here;
   *  - `MemoryGateStateStore` `String()`s the same values (`pubIdOwners.get(
   *    String(publicId))`, `graceHolds`, `connectorClients`, ...). A throwing
   *    `k()` would make redis mode reject input memory mode answers `undefined`
   *    for — a MODE-PARITY break, which is the one thing this design refuses;
   *  - the parity break would land on WIRE-FED input. `getClientByPublicId`
   *    reads its id straight off `PEER_CONNECTION_REQUEST`'s `payload[2]` and
   *    `REDIRECT_MESSAGE_TO_CLIENT`'s `msg[1]`, and today a malformed remote
   *    value simply misses the ownership index and the handler returns null.
   *    The two differ in how bad a throw would be, and only the second is
   *    severe: `PEER_CONNECTION_REQUEST` arrives through `onServerRequest`,
   *    which `BaseServer` wraps in a try/catch that turns a throw into an error
   *    RESPONSE — a degraded peer-connect, not a crash. `REDIRECT_MESSAGE_TO_
   *    CLIENT` arrives through `onServerMessage`, which is invoked floating and
   *    uncaught, so there a throw really is an unhandled rejection on a
   *    redis-backed gate.
   *
   *    The decision does not rest on that last point, though — reason 2 alone
   *    settles it. A redis gate that rejects input a memory gate answers is a
   *    mode-parity break whether or not it also crashes.
   *
   * NULLISH values are the real hazard (`String(undefined)` is the plausible-
   * looking key `'undefined'`), and they are guarded where the CALLER knows the
   * field is optional — see `RedisGateStateStore.updateAuthField`'s `oldId !=
   * null`, which mirrors the memory store's own guard. Guard at the store,
   * normalize at the wrapper.
   */
  private static k(key: string | number): string {
      return String(key);
  }

  /**
   * `options` is passed straight through to node-redis' `SET` (v4 accepts
   * `{ EX, PX, NX, XX, KEEPTTL, ... }`), so a value + its expiry can be written
   * as ONE command.
   *
   * That matters: `SET` then `EXPIRE` is two round trips, and a process that
   * dies in the gap leaves a key with NO expiry at all — i.e. exactly the
   * crash the TTL backstop exists to bound is the crash that can skip it.
   * `SET key value EX n` is atomic, so the key either does not exist or exists
   * WITH its expiry. (`SET` clears any pre-existing TTL, which is why the
   * expiry has to travel with the write rather than follow it.)
   */
  async set(key: string | number, value: any, options?: { EX?: number, PX?: number, NX?: boolean, XX?: boolean, KEEPTTL?: boolean }) : Promise<string | number> {
    const k = RedisClientWrapper.k(key);
    return options ? this.redisClient.set(k, value, options) : this.redisClient.set(k, value);
  }
  async get(key: string | number): Promise<string | number | null> {
    return this.redisClient.get(RedisClientWrapper.k(key));
  }
  async del(key: string | number): Promise<number> {
    return this.redisClient.del(RedisClientWrapper.k(key))
  }

  /** Atomic increment, returning the NEW value — the shared publicId mint. */
  async incr(key: string | number): Promise<number> {
      return this.redisClient.incr(RedisClientWrapper.k(key));
  }

  // NOTE: the client this wrapper receives is a promise-based node-redis v4
  // client (see `Init` above, which awaits `redisClient.connect()`), so these
  // are plain async pass-throughs — no callback shape. An earlier
  // callback-style `sadd(key, members, (err, reply) => ...)` sketch here was
  // dead code left over from a pre-v4 API and never actually ran; it has been
  // replaced with the real implementation below.
  //
  // THE CALLEE SPELLING IS LOAD-BEARING. node-redis v4 attaches every command
  // under its UPPERCASE name and its camelCase alias only — `SADD`/`sAdd`,
  // `RPUSH`/`rPush`, `LPOP`/`lPop`. There is no all-lowercase `sadd`/`smembers`/
  // `rpush`/`lpop` on a real client (single-word commands like `get`/`set`/
  // `del`/`expire` look lowercase only because their camelCase alias IS
  // lowercase). Calling the all-lowercase form throws
  // `TypeError: this.redisClient.sadd is not a function` against a real daemon —
  // which is exactly what these six did until the real-redis suite ran them:
  // the connector→clients index and the sequential publicId pool, i.e. every
  // set and list op the gate makes, were unreachable on real redis while the
  // fake (which had been written to mirror THIS file's spelling rather than the
  // real client's) reported them green. Introduced during this effort
  // (00b43656/652a2dc7) and caught before release — see task-7-report.md.
  async sadd(key: string | number, members: Array<string | number>): Promise<number> {
      return this.redisClient.sAdd(RedisClientWrapper.k(key), members.map(m => String(m)));
  }

  async srem(key: string | number, members: Array<string | number>): Promise<number> {
      return this.redisClient.sRem(RedisClientWrapper.k(key), members.map(m => String(m)));
  }

  async smembers(key: string | number): Promise<Array<string | number>> {
      return this.redisClient.sMembers(RedisClientWrapper.k(key));
  }

  async scard(key: string | number): Promise<number> {
      return this.redisClient.sCard(RedisClientWrapper.k(key));
  }

  async rpush(key: string | number, values: Array<string | number>): Promise<number> {
      return this.redisClient.rPush(RedisClientWrapper.k(key), values.map(v => String(v)));
  }

  async lpop(key: string | number): Promise<string | null> {
      return this.redisClient.lPop(RedisClientWrapper.k(key));
  }

  /** `seconds` matches the real EXPIRE command's unit — SECONDS, not ms. */
  async expire(key: string | number, seconds: number): Promise<boolean> {
      return this.redisClient.expire(RedisClientWrapper.k(key), seconds);
  }

    async hset(key: string | number, keyOrValue: string | number | any, value?: string | number): Promise<number> {
     // The scalar form's FIELD is normalized like every other field; the object
     // form is left alone (HSET's own transform converts both halves of each
     // pair, and rewriting the object here would be a pointless copy).
     const field = (keyOrValue !== null && typeof keyOrValue === 'object') ? keyOrValue : String(keyOrValue);
     return this.redisClient.HSET(RedisClientWrapper.k(key), field, value)
    }

    async hmget(key: string | number, field: (string | number)[]): Promise<any[]> {
        return this.redisClient.HMGET(RedisClientWrapper.k(key), field.map(f => String(f)))
    }
    async hget(key: string | number, field: string | number): Promise<string | number | null> {
        return this.redisClient.HGET(RedisClientWrapper.k(key), String(field))
    }

    async hgetall(key: string | number): Promise<any> {
        return this.redisClient.HGETALL(RedisClientWrapper.k(key))
    }

  async hdel(key: string | number, fields: Array<string | number> | string | number): Promise<number> {
    const normalized = Array.isArray(fields) ? fields.map(f => String(f)) : String(fields);
    return  this.redisClient.HDEL(RedisClientWrapper.k(key), normalized)
  }
}
